import { useCallback } from "react";
import { useClasses } from "@api/class.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useBulkActions } from "./useBulkActions";

interface UseClassCommandOptionsParams {
    // Selected transaction ids the class update should actually target,
    // excluding any matched to an invoice or bill (class assignments are
    // locked server-side on those — see MatchedTransactionFieldLockedError /
    // partitionSelectionByInvoiceOrBillLock). Undefined means "target the
    // whole current selection", matching the pre-existing behavior.
    unlockedTransactionIds?: number[];
}

export function useClassCommandOptions({
    unlockedTransactionIds,
}: UseClassCommandOptionsParams = {}) {
    const bulkActions = useBulkActions();
    const { classesWithChildren, classesMap } = useClasses();
    const { error: toastError } = useToaster();

    const applyClasses = useCallback(
        async (classIds: string[]) => {
            try {
                await bulkActions.updateSelected(
                    {
                        update: "classes",
                        value: classIds,
                    },
                    unlockedTransactionIds
                        ? { targetTransactionIds: unlockedTransactionIds }
                        : undefined,
                );
            } catch {
                toastError("Failed to assign classes");
            }
        },
        [bulkActions, toastError, unlockedTransactionIds],
    );

    return { classesWithChildren, classesMap, applyClasses };
}
